// Utility functions for asset form handling
// Centralized logic for initial values, AI extraction mapping, and payload building

import { getFieldConfig } from "../components/asset-forms/config/fieldConfigs";

/**
 * Normalize frequency values from AI extraction
 * Handles typos and case variations
 * @param {string} value - Raw frequency value from AI
 * @returns {string} Normalized frequency value
 */
export const normalizeFrequency = (value) => {
  if (!value) return '';

  const normalized = value.toLowerCase().trim();

  // Map common AI typos and variations to correct values
  const frequencyMap = {
    'montly': 'monthly',
    'month': 'monthly',
    'monthely': 'monthly',
    'monthly': 'monthly',
    'quarterly': 'quarterly',
    'quarter': 'quarterly',
    'yearly': 'yearly',
    'year': 'yearly',
    'annual': 'yearly',
    'annually': 'yearly',
    'halfyearly': 'halfyearly',
    'half yearly': 'halfyearly',
    'half-yearly': 'halfyearly',
    'semiannual': 'halfyearly',
    'weekly': 'weekly',
    'week': 'weekly',
    'fortnightly': 'fortnightly',
    'biweekly': 'fortnightly',
    'daily': 'daily',
    'day': 'daily'
  };

  return frequencyMap[normalized] || normalized;
};

/**
 * Normalize insurance type values
 * @param {string} value - Raw insurance type from AI
 * @returns {string} Normalized insurance type
 */
export const normalizeInsuranceType = (value) => {
  if (!value) return '';

  const normalized = value.toLowerCase().trim();

  const typeMap = {
    'helth': 'health',
    'health': 'health',
    'life insurence': 'life',
    'life': 'life',
    'car': 'auto',
    'automobile': 'auto',
    'vehicle': 'auto',
    'auto': 'auto',
    'house': 'home',
    'property': 'home',
    'home': 'home',
    'traveling': 'travel',
    'trip': 'travel',
    'travel': 'travel'
  };

  return typeMap[normalized] || normalized;
};

/**
 * Normalize vehicle type values
 * @param {string} value - Raw vehicle type from AI
 * @returns {string} Normalized vehicle type
 */
export const normalizeVehicleType = (value) => {
  if (!value) return '';

  const normalized = value.toLowerCase().trim();

  const typeMap = {
    'automobil': 'car',
    'automobile': 'car',
    'sedan': 'car',
    'hatchback': 'car',
    'coupe': 'car',
    'car - sedan': 'car',
    'car - hatchback': 'car',
    'car - coupe': 'car',
    'car - suv': 'suv',
    'bike': 'motorcycle',
    'motorbike': 'motorcycle',
    'scooter': 'motorcycle',
    'motorcycle - bike': 'motorcycle',
    'motorcycle - scooter': 'motorcycle',
    'pickup': 'truck',
    'lorry': 'truck',
    'truck - pickup': 'truck',
    'minivan': 'van',
    'van - minivan': 'van',
    'suv': 'suv',
    'sport utility vehicle': 'suv'
  };

  return typeMap[normalized] || normalized;
};

/**
 * Normalize blockchain network values
 * @param {string} value - Raw network from AI
 * @returns {string} Normalized network
 */
export const normalizeBlockchain = (value) => {
  if (!value) return '';

  const normalized = value.toLowerCase().trim();

  const networkMap = {
    'btc': 'bitcoin',
    'eth': 'ethereum',
    'etherium': 'ethereum',
    'sol': 'solana',
    'matic': 'polygon',
    'binance': 'bsc',
    'binance smart chain': 'bsc',
    'bnb': 'bsc',
    'avax': 'avalanche'
  };

  return networkMap[normalized] || normalized;
};

/**
 * Generate initial values for a given asset subcategory
 * @param {string} subCategory - Asset subcategory (e.g., "insurance", "stocks")
 * @param {string} defaultCurrency - Default currency code (e.g., "INR", "USD")
 * @returns {object} Initial form values
 */
export const getInitialValuesForAsset = (subCategory, defaultCurrency = "INR") => {
  const config = getFieldConfig(subCategory);

  // If config has custom getInitialValues function, use it
  if (config.getInitialValues) {
    return config.getInitialValues(defaultCurrency);
  }

  // Fallback: auto-generate from field definitions
  return generateDefaultValuesFromFields(config, defaultCurrency, subCategory);
};

/**
 * Auto-generate default values from field configuration
 * @param {object} config - Field configuration object
 * @param {string} defaultCurrency - Default currency code
 * @param {string} subCategory - Asset subcategory
 * @returns {object} Default form values
 */
const generateDefaultValuesFromFields = (config, defaultCurrency, subCategory) => {
  const today = new Date().toISOString().slice(0, 10);
  const allFields = [
    ...(config.commonFields || []),
    ...(config.specificFields || []),
    ...(config.sidebarFields || [])
  ];

  // Determine asset category based on subcategory
  const physicalAssets = ['cash', 'property', 'vehicle', 'collectibles', 'precious-metals', 'other-physical-assets'];
  const assetCategory = physicalAssets.includes(subCategory) ? 'physical' : 'digital';

  const initialValues = {
    purchaseCurrency: defaultCurrency,
    isActive: "yes",
    subCategory: subCategory,  // Required by base validation schema
    assetCategory: assetCategory  // digital or physical
  };

  allFields.forEach(field => {
    const fieldName = field.name;

    // Handle nested fields (e.g., "rentalIncome.amount")
    if (fieldName.includes('.')) {
      const [parent, child] = fieldName.split('.');
      if (!initialValues[parent]) {
        initialValues[parent] = {};
      }
      initialValues[parent][child] = getDefaultValueForFieldType(field);
    } else {
      // Special handling for common fields
      if (fieldName === 'purchaseDate') {
        initialValues[fieldName] = today;
      } else if (fieldName === 'purchaseCurrency') {
        initialValues[fieldName] = defaultCurrency;
      } else {
        initialValues[fieldName] = getDefaultValueForFieldType(field);
      }
    }
  });

  return initialValues;
};

/**
 * Get default value based on field type
 * @param {object} field - Field configuration
 * @returns {any} Default value for the field type
 */
const getDefaultValueForFieldType = (field) => {
  switch (field.type) {
    case 'checkbox':
      return false;
    case 'number':
    case 'currency':
      return '';
    case 'radio':
      return field.options?.[0]?.value || '';
    case 'select':
      return '';
    case 'date':
      return '';
    case 'textarea':
    case 'text':
    default:
      return '';
  }
};

/**
 * Fill form fields from AI-extracted data
 * @param {object} extractedItem - Data extracted from document by AI
 * @param {function} setFieldValue - Formik setFieldValue function
 * @param {string} subCategory - Asset subcategory
 */
export const fillExtractedFields = (extractedItem, setFieldValue, subCategory) => {
  if (!extractedItem || !setFieldValue) return {};

  const config = getFieldConfig(subCategory);

  // Track warnings for invalid dropdown values
  const warnings = {};

  // If config has custom extractionMapper, use it
  if (config && config.extractionMapper) {
    const mappedData = config.extractionMapper(extractedItem);

    // Safety check - ensure mappedData is an object
    if (!mappedData || typeof mappedData !== 'object') {
      console.warn(`extractionMapper for ${subCategory} returned invalid data:`, mappedData);
      return warnings;
    }

    Object.entries(mappedData).forEach(([fieldName, value]) => {
      if (value !== undefined && value !== null) {
        setFieldValue(fieldName, value);
      }
    });
  }

  return warnings;
};

/**
 * Build API payload from form values using config
 * @param {object} values - Form values
 * @param {string} subCategory - Asset subcategory
 * @param {object} commonData - Common data (tags, notes, photos, documents)
 * @returns {object} API payload
 */
export const buildAssetPayload = (values, subCategory, commonData = {}) => {
  const config = getFieldConfig(subCategory);

  // Base payload with common fields
  const payload = {
    userId: commonData.userId || '',
    name: values.name || '',
    purchaseDate: values.purchaseDate || '',
    purchasePrice: values.purchasePrice || '',
    purchaseCurrency: values.purchaseCurrency || 'INR',
    assetCategory: values.assetCategory || commonData.assetCategory || 'digital',  // Use correct assetCategory field
    assetType: commonData.assetType || subCategory,
    isActive: values.isActive || 'yes',
    notes: values.notes || '',
    tags: commonData.tags || [],
    photos: commonData.photos || [],
    documents: commonData.documents || [],
    description: values.description || values.notes || ''
  };

  // Add estimated value (various fields map to this)
  payload.estimatedValue = values.estimatedValue
    || values.currentEstimateValue
    || values.estimatedCurrentValue
    || values.purchasePrice;

  // If config has custom buildPayload function, use it for details
  if (config.buildPayload) {
    const { detailsKey, detailsData } = config.buildPayload(values);
    payload[detailsKey] = detailsData;

    // Update estimatedValue if details provide it
    if (detailsData.estimatedCurrentValue) {
      payload.estimatedValue = detailsData.estimatedCurrentValue;
    }
  }

  return payload;
};

/**
 * Map API response to form values for edit mode
 * @param {object} apiData - Asset data from API
 * @param {string} subCategory - Asset subcategory
 * @returns {object} Form values
 */
export const mapApiResponseToFormValues = (apiData, subCategory) => {
  const config = getFieldConfig(subCategory);

  // Base form values from common fields
  const formValues = {
    name: apiData.name || '',
    purchaseDate: apiData.purchaseDate ? apiData.purchaseDate.slice(0, 10) : '',
    purchasePrice: apiData.purchasePrice || '',
    purchaseCurrency: apiData.purchaseCurrency || 'INR',
    notes: apiData.notes || '',
    isActive: apiData.isActive || 'yes',
    assetCategory: apiData.assetCategory || 'digital',
    assetType: apiData.assetType || subCategory,
    currentValue: apiData.purchasePrice || '',
    estimatedValue: apiData.estimatedValue || ''
  };

  // If config has custom responseMapper, use it for details
  if (config.responseMapper) {
    const detailsMapped = config.responseMapper(apiData);
    Object.assign(formValues, detailsMapped);
  } else {
    // Fallback: auto-map from detailsKey
    const detailsKey = config.api?.detailsKey;
    if (detailsKey && apiData[detailsKey]) {
      Object.assign(formValues, apiData[detailsKey]);
    }
  }

  return formValues;
};

/**
 * Build update payload for edit mode (PATCH request)
 * @param {object} values - Current form values
 * @param {object} initialValues - Original values before edit
 * @param {string} subCategory - Asset subcategory
 * @param {object} commonData - Common data (tags, photos, documents)
 * @returns {object} PATCH payload with only changed fields
 */
export const buildUpdatePayload = (values, initialValues, subCategory, commonData = {}) => {
  const config = getFieldConfig(subCategory);
  const changedPayload = {};

  // Excluded keys that are handled separately
  const excludedKeys = ['currentValue', 'assetCategory', 'assetType'];

  // Check for changes in common fields
  Object.keys(values).forEach(key => {
    if (excludedKeys.includes(key)) return;

    if (key === 'purchasePrice') {
      changedPayload['purchasePrice'] = values['purchasePrice'];
    } else if (initialValues && values[key] !== initialValues[key]) {
      changedPayload[key] = values[key];
    }
  });

  // If config has custom buildPayload, use it for details
  if (config.buildPayload) {
    const { detailsKey, detailsData } = config.buildPayload(values);
    changedPayload[detailsKey] = detailsData;

    // Update estimatedValue if details provide it
    if (detailsData.estimatedCurrentValue) {
      changedPayload.estimatedValue = detailsData.estimatedCurrentValue;
    } else if (detailsData.currentEstimateValue) {
      changedPayload.estimatedValue = detailsData.currentEstimateValue;
    }
  }

  // Add photos if changed
  if (commonData.photos && JSON.stringify(commonData.photos) !== JSON.stringify(initialValues?.photos)) {
    changedPayload.photos = commonData.photos;
  }

  // Add documents if changed
  if (commonData.documents && JSON.stringify(commonData.documents) !== JSON.stringify(initialValues?.documents)) {
    changedPayload.documents = commonData.documents;
  }

  // Add tags if changed
  if (commonData.tags && JSON.stringify(commonData.tags) !== JSON.stringify(initialValues?.tags)) {
    changedPayload.tags = commonData.tags;
  }

  return changedPayload;
};

/**
 * Get display label for asset type
 * @param {string} subCategory - Asset subcategory
 * @returns {string} Display label
 */
export const getAssetTypeLabel = (subCategory) => {
  const labels = {
    'insurance': 'Insurance',
    'vehicle': 'Vehicle',
    'cash': 'Cash',
    'property': 'Property',
    'bank-accounts': 'Bank Accounts',
    'mutual-funds': 'Mutual Funds',
    'fixed-deposit': 'Fixed Deposit',
    'stocks': 'Stocks',
    'crypto': 'Crypto',
    'collectibles': 'Collectibles',
    'business-ownership': 'Business Ownership',
    'other-physical-assets': 'Other Physical Assets',
    'important-papers': 'Important Papers',
    'precious-metals': 'Precious Metals'
  };

  return labels[subCategory] || subCategory;
};

/**
 * Map subcategory to API asset type value
 * @param {string} subCategory - UI subcategory name
 * @returns {string} API asset type value
 */
export const mapSubCategoryToApiType = (subCategory) => {
  const mapping = {
    'bank-accounts': 'bankAccounts',
    'mutual-funds': 'mutualFunds',
    'fixed-deposit': 'fixedDeposits',
    'property': 'homeProperty',
    'vehicle': 'vehicles',
    'business-ownership': 'businessOwnership',
    'other-physical-assets': 'otherPhysicalAssets',
    'important-papers': 'importantPapers',
    'precious-metals': 'preciousMetals'
  };

  return mapping[subCategory] || subCategory;
};
